import type { Asset, Job } from "@studio/shared";
import type { QueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { type CreateKind, router } from "@/router";
import { api } from "./api";
import { loadJobIntoComposer, type Slot, useComposer } from "./composer";
import { useLive } from "./events";

/** The verbs every result offers, wherever it is shown (studio, chat, library, viewer). */

export function kindOfJob(job: Job): CreateKind {
  return job.type;
}

export async function moreLikeThis(job: Job): Promise<void> {
  try {
    const request = { ...job.request, seed: undefined };
    const created = await api.createJob({ request, parentJobId: job.id });
    useLive.getState().upsertJob(created);
    toast.success("Making more like this");
  } catch (error) {
    toast.error((error as Error).message);
  }
}

export async function runAgain(job: Job): Promise<void> {
  try {
    const created = await api.createJob({ request: job.request, parentJobId: job.id });
    useLive.getState().upsertJob(created);
    toast.success("Started again");
  } catch (error) {
    toast.error((error as Error).message);
  }
}

/** A result that skipped steps, made again with every step: the same request and seed, speed "quality". */
export async function redoAtFullQuality(job: Job): Promise<void> {
  if (job.request.type !== "image" && job.request.type !== "video") return;
  try {
    const request = { ...job.request, speed: "quality" as const };
    const created = await api.createJob({ request, parentJobId: job.id });
    useLive.getState().upsertJob(created);
    toast.success("Making it again with every step");
  } catch (error) {
    toast.error((error as Error).message);
  }
}

/**
 * Whether a finished result can be made again at full quality: it ran fast, and no full-quality redo of it is made
 * or on its way (a 2K redo takes twenty minutes; one is enough).
 */
export function canRedoAtFullQuality(job: Job, jobs: Record<string, Job>): boolean {
  if (job.status !== "completed" || job.ranSpeed !== "fast") return false;
  return !Object.values(jobs).some(
    (j) =>
      j.parentJobId === job.id &&
      j.status !== "failed" &&
      j.status !== "cancelled" &&
      (j.request.type === "image" || j.request.type === "video") &&
      j.request.speed === "quality",
  );
}

export async function editJob(job: Job, queryClient: QueryClient): Promise<void> {
  await loadJobIntoComposer(job, (id) =>
    queryClient.fetchQuery({ queryKey: ["asset", id], queryFn: () => api.asset(id) }),
  );
  await router.navigate({ to: "/create", search: { kind: kindOfJob(job) } });
  toast("Change what you like and create again");
}

/** Opens the Enhance tab with this picture or video as the file to restore and enlarge. */
export async function enhanceAsset(asset: Asset): Promise<void> {
  const note = useComposer.getState().setEnhanceSource(asset);
  if (note) {
    toast.error(note);
    return;
  }
  await router.navigate({ to: "/create", search: { kind: "enhance" } });
  toast.success("Added to Enhance", {
    description: "Pick what to do with it and how much larger it should be, then press Enhance.",
  });
}

/** The same for the one result of a finished picture or video. */
export async function enhanceResult(job: Job, queryClient: QueryClient): Promise<void> {
  const id = job.resultAssetIds[0];
  if (!id) return;
  try {
    await enhanceAsset(
      await queryClient.fetchQuery({ queryKey: ["asset", id], queryFn: () => api.asset(id) }),
    );
  } catch (error) {
    toast.error((error as Error).message);
  }
}

const SLOT_WORDS: Record<Slot, string> = {
  first: "the start frame",
  last: "the end frame",
  reference: "a reference",
};

/** Opens the image editor with this picture as Qwen's source. */
export async function addToImage(asset: Asset): Promise<void> {
  const note = useComposer.getState().addImageReference(asset);
  if (note) {
    toast.error(note);
    return;
  }
  await router.navigate({ to: "/create", search: { kind: "image" } });
  toast.success("Added to a Qwen image edit", {
    description: "Draw on it to mark a region, or describe the change.",
  });
}

export async function addToVideo(asset: Asset, slot: Slot): Promise<void> {
  const note = useComposer.getState().placeInVideo(asset, slot);
  const failed = note && !note.endsWith("cleared: a video uses frames or references.");
  if (failed) {
    toast.error(note);
    return;
  }
  await router.navigate({ to: "/create", search: { kind: "video" } });
  toast.success(`Added as ${SLOT_WORDS[slot]}`, note ? { description: note } : undefined);
}

export async function saveFrame(
  asset: Asset,
  timeSec: number,
  queryClient: QueryClient,
): Promise<Asset | null> {
  try {
    const frame = await api.grabFrame(asset.id, timeSec);
    queryClient.invalidateQueries({ queryKey: ["assets"] });
    toast.success("Frame saved to your library", {
      action: { label: "Animate it", onClick: () => void addToVideo(frame, "first") },
    });
    return frame;
  } catch (error) {
    toast.error((error as Error).message);
    return null;
  }
}

/** The whole of what the model made, where the studio plays a smaller or converted copy (Asset.originalUrl). */
export function downloadOriginal(asset: Asset): void {
  if (!asset.originalUrl) return;
  const link = document.createElement("a");
  link.href = asset.originalUrl;
  link.download = "";
  document.body.appendChild(link);
  link.click();
  link.remove();
}

export function download(asset: Asset): void {
  const link = document.createElement("a");
  link.href = `${asset.url}${asset.url.includes("?") ? "&" : "?"}download=1`;
  link.download = "";
  document.body.appendChild(link);
  link.click();
  link.remove();
}
